import { useMsal } from "@azure/msal-react";
import {
    Box,
    Button,
    Center,
    Container,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title,
} from "@mantine/core";
import { IconCurrencyDollar, IconKey, IconLock, IconNorthStar } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { RequestAccessModal } from "src/components/RequestAccessModal";
import { getAPI } from "src/utils/fetch";
import { useNotAuthorizedUser } from "src/utils/useAdmin";

export const Route = createFileRoute("/_layout-nosidebar/unauthorized")({
    component: UnauthorizedRoute,
});
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
export function UnauthorizedRoute() {
    const environment = import.meta.env.VITE_ENVIRONMENT;
    const [countdown, setCountdown] = useState(3);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const navigate = useNavigate();
    const { instance } = useMsal();
    const [hasPendingRequests, setHasPendingRequests] = useState(false);
    const isNotAuthorized = useNotAuthorizedUser();

    const shouldRedirectToProduction = environment === "dev" || environment === "qa";
    const GUI_URL_PROD = "https://ads-deep.mosaic.siemens-energy.cloud";
    useEffect(() => {
        if (!isNotAuthorized) {
            navigate({ to: "/" });
            return;
        }

        if (shouldRedirectToProduction) {
            setIsRedirecting(true);

            const countdownInterval = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(countdownInterval);
                        window.location.href = GUI_URL_PROD;
                        return 0;
                    }
                    return prev - 1;
                });
            }, 2000);

            return () => clearInterval(countdownInterval);
        }

        return undefined;
    }, [shouldRedirectToProduction, isNotAuthorized, navigate]);

    const fetchPendingRequests = async () => {
        try {
            const response = await getAPI({
                url: `${BACKEND_URL}/v1/user/access-requests`,
                instance,
            });
            const requests = await response.json();
            setHasPendingRequests(requests.length > 0);
        } catch (error) {
            console.error("Failed to fetch pending requests:", error);
        }
    };

    if (!isRedirecting) {
        fetchPendingRequests();
    }

    if (isRedirecting) {
        return (
            <Center py="xl" style={{ height: "100%" }}>
                <Container size="sm">
                    <Box ta="center" py="xl">
                        <Title>Redirecting to CTS Knowledge Search</Title>
                        <Text mb="md" size="lg">
                            You are being redirected to the Production Environment in {countdown}{" "}
                            seconds...
                        </Text>
                        <Text size="sm" mb="lg">
                            This is a {environment.toUpperCase()} environment. For the latest stable
                            version, please use the production environment.
                        </Text>
                        <Button component="a" href={GUI_URL_PROD} variant="filled">
                            Go to Production Now
                        </Button>
                    </Box>
                </Container>
            </Center>
        );
    }

    return (
        <Center py="xl" style={{ height: "100%" }}>
            <Container size="sm">
                <Title variant="heading1">CTS Knowledge Search</Title>
                <Text mt={4} size="xl">
                    We help you find the information you need.
                </Text>
                <SimpleGrid mt={50} cols={{ base: 1, md: 3 }} spacing={30}>
                    {features.map((feature) => (
                        <div key={feature.title}>
                            <ThemeIcon variant="outline" size={50} radius={50}>
                                <feature.icon size={26} stroke={1.5} />
                            </ThemeIcon>
                            <Text mt="sm" mb={7}>
                                {feature.title}
                            </Text>
                            <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
                                {feature.description}
                            </Text>
                        </div>
                    ))}
                </SimpleGrid>
                <Stack mt={50}>
                    <Text
                        size="xl"
                        fw={600}
                        style={{ display: hasPendingRequests ? "block" : "none" }}
                    >
                        You already requested access - we're working on it!
                    </Text>
                    <RequestAccessModal>
                        <Button
                            size="md"
                            variant="filled"
                            leftSection={<IconKey size={20} />}
                            disabled={hasPendingRequests}
                        >
                            Request Access
                        </Button>
                    </RequestAccessModal>
                </Stack>
            </Container>
        </Center>
    );
}

const features = [
    {
        icon: IconCurrencyDollar,
        title: "Increase Productivity",
        description:
            "Leverage our Knowledge Search tool to handle Salesforce cases and field service reports effortlessly. Save time by getting instant answers, summaries, and insights without reading through lengthy documents.",
    },
    {
        icon: IconLock,
        title: "Enhanced Privacy",
        description:
            "Your data security is our top priority. With no tracking, no cookies, and complete local storage, our tool ensures that your sensitive information remains confidential and protected.",
    },
    {
        icon: IconNorthStar,
        title: "Superior Support Experience",
        description:
            "Experience unparalleled efficiency with a tool designed to help you provide exceptional support. Our copilot tool enhances your ability to assist end users, ensuring a seamless and productive workflow.",
    },
];
